/**
 * Offline forecast cache. Same 24h window as iOS; localStorage instead of AsyncStorage.
 */

import type { HourlyWeatherSample, LatLng, RouteWaypoint, TripEndpointRow, WeatherAlert, OSRMRoute } from '../../types';

export const OFFLINE_CACHE_SCHEMA_VERSION = 1;
export const OFFLINE_CACHE_STORAGE_KEY = 'ontheway_offline_forecast_cache_v1';
export const MAX_CACHE_AGE_MS = 24 * 60 * 60 * 1000;

export type SerializedHourlyWeatherSample = Omit<HourlyWeatherSample, 'time'> & { time: string };

export type OfflineForecastCache = {
  version: number;
  cachedAtMs: number;
  cachedAtStr: string;
  departureIso: string;
  routes: OSRMRoute[];
  selectedRouteIndex: number;
  waypoints: RouteWaypoint[];
  tripEndpoints: TripEndpointRow[];
  hourlyByCoordKey: Record<string, SerializedHourlyWeatherSample[]>;
  alerts: WeatherAlert[];
};

export function sampleToSerialized(s: HourlyWeatherSample): SerializedHourlyWeatherSample {
  return { ...s, time: s.time instanceof Date ? s.time.toISOString() : String(s.time) };
}

export function serializedToSample(s: SerializedHourlyWeatherSample): HourlyWeatherSample {
  return { ...s, time: new Date(s.time) };
}

export function serializeOfflineCache(cache: OfflineForecastCache): string {
  return JSON.stringify(cache);
}

export function deserializeOfflineCache(raw: string | null | undefined): OfflineForecastCache | null {
  if (!raw || typeof raw !== 'string') return null;
  try {
    const parsed = JSON.parse(raw) as Partial<OfflineForecastCache>;
    if (parsed.version !== OFFLINE_CACHE_SCHEMA_VERSION) return null;
    if (typeof parsed.cachedAtMs !== 'number' || !Number.isFinite(parsed.cachedAtMs)) return null;
    if (typeof parsed.departureIso !== 'string') return null;
    if (!Array.isArray(parsed.routes) || !Array.isArray(parsed.waypoints) || !Array.isArray(parsed.tripEndpoints)) return null;
    if (!parsed.hourlyByCoordKey || !Array.isArray(parsed.alerts)) return null;
    return parsed as OfflineForecastCache;
  } catch {
    return null;
  }
}

export function pruneOfflineCache(cache: OfflineForecastCache, nowMs: number, maxAgeMs = MAX_CACHE_AGE_MS): OfflineForecastCache | null {
  const age = nowMs - cache.cachedAtMs;
  if (age > maxAgeMs || age < 0) return null;
  return cache;
}

export function saveOfflineForecast(cache: OfflineForecastCache): void {
  try {
    localStorage.setItem(OFFLINE_CACHE_STORAGE_KEY, serializeOfflineCache(cache));
  } catch {
    // quota
  }
}

export function loadOfflineForecast(nowMs = Date.now()): OfflineForecastCache | null {
  try {
    const deserialized = deserializeOfflineCache(localStorage.getItem(OFFLINE_CACHE_STORAGE_KEY));
    if (!deserialized) return null;
    const pruned = pruneOfflineCache(deserialized, nowMs);
    if (!pruned) {
      localStorage.removeItem(OFFLINE_CACHE_STORAGE_KEY);
      return null;
    }
    return pruned;
  } catch {
    return null;
  }
}

export function hourlyMapFromCache(cache: OfflineForecastCache): Map<string, HourlyWeatherSample[]> {
  const map = new Map<string, HourlyWeatherSample[]>();
  for (const [key, rows] of Object.entries(cache.hourlyByCoordKey)) {
    map.set(key, rows.map(serializedToSample));
  }
  return map;
}

export function hourlyMapToRecord(map: Map<string, HourlyWeatherSample[]>): Record<string, SerializedHourlyWeatherSample[]> {
  const out: Record<string, SerializedHourlyWeatherSample[]> = {};
  map.forEach((rows, key) => {
    out[key] = rows.map(sampleToSerialized);
  });
  return out;
}

export function coordFromCache(_c: LatLng): LatLng {
  return _c;
}
